import { Transition, TransitionChild } from "@headlessui/react";
import clsx from "clsx";
import FocusTrap from "focus-trap-react";
import React, {
  FunctionComponent,
  Fragment,
  ComponentProps,
  useState,
  useEffect,
  useRef,
} from "react";

import { sharedStyles } from "@ui/shared-styles";

import * as styles from "./Drawer.css";

interface Props {
  drawerClassName?: string;
  open: boolean;
  onClose: () => void;
  children?: React.ReactNode;
  /** Render statically for storybook and chromatix */
  storybookStatic?: boolean;
  drawerId: string;
  initialFocusElement?: HTMLElement | null | false;
  "aria-label": string;
  scrollToTop?: boolean;
  disableTransition?: boolean;
}

const Drawer: FunctionComponent<Props> = ({
  children,
  drawerId,
  drawerClassName,
  onClose,
  open,
  storybookStatic,
  initialFocusElement,
  "aria-label": ariaLabel,
  scrollToTop = true,
  disableTransition,
}) => {
  const drawerTransition: Partial<ComponentProps<typeof TransitionChild>> = {
    enter: styles.transitionDrawerEnter,
    enterFrom: styles.transitionDrawerEnterFrom,
    enterTo: styles.transitionDrawerEnterTo,
    leave: styles.transitionDrawerLeave,
    leaveFrom: styles.transitionDrawerLeaveFrom,
    leaveTo: styles.transitionDrawerLeaveTo,
  };

  // Put initialFocus prop in a ref, because the TrapFocus callback
  // doesn't seem to be able to access prop changes after initialization.
  const initialFocusRef = useRef(initialFocusElement);
  initialFocusRef.current = initialFocusElement;

  // In contrast to `open`, `visible` only changes state after the transition.
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (open && scrollToTop) {
      document.getElementById(`${drawerId}-scroll`)?.scrollTo({ top: 0 });
    }
  }, [drawerId, open, scrollToTop]);

  return (
    <Transition show={open} as={Fragment} unmount={false}>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        className={clsx(
          styles.dialog,
          storybookStatic && styles.positionStatic,
        )}
        id={drawerId}
        onClick={(e) => {
          if (e.currentTarget === e.target) {
            onClose();
          }
        }}
        onKeyUp={(e) => {
          if (e.key === "Escape") {
            onClose();
          }
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${drawerId}-title`}
      >
        <TransitionChild
          as={Fragment}
          className={disableTransition ? styles.disableTransition : undefined}
          {...drawerTransition}
          unmount={false}
          afterEnter={() => setVisible(true)}
          afterLeave={() => setVisible(false)}
        >
          <section
            className={clsx(
              styles.drawer,
              drawerClassName,
              storybookStatic && styles.positionStatic,
            )}
            id={`${drawerId}-scroll`}
          >
            <div className={sharedStyles.accessibility.srOnly}>
              <div id={`${drawerId}-title`}>{ariaLabel}</div>
            </div>
            <FocusTrap
              active={visible}
              focusTrapOptions={{
                escapeDeactivates: false,
                clickOutsideDeactivates: false,
                allowOutsideClick: true,
                preventScroll: true,
                initialFocus: () => {
                  return initialFocusRef.current
                    ? initialFocusRef.current
                    : undefined;
                },
              }}
            >
              <div>{children}</div>
            </FocusTrap>
          </section>
        </TransitionChild>
      </div>
    </Transition>
  );
};

export default Drawer;
